import type { ComponentProps, KeyboardEvent, MouseEvent, ReactNode, Ref } from 'react';
import clsx from 'clsx';
import { Icon } from '@clay-run/glaze/icon';
import { Strong } from '@clay-run/glaze/text';

/** Attribute the queue's scroll+flash effect (and its tests) query a row by. One row per contact in practice (the queue dedups per contact server-side). */
export const FLASH_CONTACT_ATTR = 'data-flash-contact';

/** Rounded hover surface shared by every interactive rail row (the Plays section header, flat rows). */
export const RAIL_ROW_SURFACE = 'rounded-lg transition-colors hover:bg-bg-secondary-hover';
/** Hairline between stacked rows/sections in the rail. */
export const RAIL_ROW_DIVIDER = 'border-t-[0.5px] border-border-primary';
/** Vertical inset between a row's divider and its rounded hover surface. */
export const RAIL_ROW_INSET_Y = 'py-0.5';
/** Minimum height of a row's interactive strip. With the inset and divider above this is the one row pitch: 64 + 2*2 + 0.5 = 68.5px. */
export const RAIL_ROW_STRIP_MIN_H = 'min-h-16';

/**
 * The V2 rail's one bordered-row primitive (plan 2026-10-01-002 dispatch
 * unit U1). `LeadRow` and `UnassignedRow` both build on this rather than
 * hand-rolling their own `rounded-lg border` box, so the two row families in
 * the Plays pane read as one visual system.
 *
 * This IS the prototype's `Element.tsx` analog now, minus motion — same
 * leading/title/meta/trailing slot shape, same `icon`-in-an-unfilled-box vs.
 * `leading`-wins-over-`icon` precedence. One deliberate deviation: this row
 * uses `items-start` on the header row, not the prototype's `items-center` —
 * Terra's `meta` slot carries multi-line status blocks (warm-intro warnings,
 * Gong state), so vertical-centering the leading icon against a tall meta
 * block would float it away from the title.
 *
 * Toggle semantics are opt-in (`onToggle`): `LeadRow` passes it (the row
 * toggles `expandedItemId`, D19); `UnassignedRow` does not — its own
 * `Assign to play…` control is the only toggle affordance QueueBody's V1
 * counterpart has ever had, and D22 doesn't add a row-level one. When
 * `onToggle` is passed but `toggleDisabled` is true (E28 — a busy row), the
 * row renders with no toggle semantics at all (no `role="button"`, no
 * `tabIndex`), matching `AssignedRow`'s own `itemBusy` early-return posture
 * rather than a toggle that silently no-ops.
 */

/**
 * CLICK delegation guard, ported from `AssignedRow.tsx`'s row-click handler
 * (D31 "delegation guard" comment): every real control inside the row's
 * trailing slot (or a nested overlay like `UnassignedRow`'s inline
 * `PlayPicker`) is or is inside one of these, so a single `closest()` check
 * keeps the row's own toggle from firing when the rep is actually clicking a
 * button/input/link/dialog — without each individual control needing its own
 * `stopPropagation`. `[data-no-row-toggle]` is the escape hatch for a
 * non-interactive wrapper (e.g. a picker's own padding) that must also not
 * trigger the row toggle, AND the hook for a composite control whose visible
 * element isn't itself a native control this selector would otherwise catch
 * — e.g. `LeadGongControls`' Glaze/Base-UI checkbox, whose visible element
 * is a `<span role="checkbox">` wrapped by a `<label>`, neither of which
 * matches anything else in this list.
 *
 * `[contenteditable]:not([contenteditable="false"])` covers `RichHtmlField`
 * (`components/ui/RichHtmlField.tsx`), the expanded body's Gong step-body
 * editor — a plain `<div contentEditable>` with no `stopPropagation` of its
 * own, reached via `LeadExpandedBody` -> `EnrollmentItemEditor` -> `StepRows`
 * -> `StepRow`. Without this entry, a click placing the caret bubbles past
 * every other item in this selector, matches nothing, and falls through to
 * the row toggle below — collapsing the row and unmounting the editor
 * mid-edit. `RichHtmlField` doesn't set an explicit `role="textbox"`, so the
 * attribute selector (not a role selector) is the only hook; the
 * `:not([contenteditable="false"])` qualifier excludes `RichHtmlField`'s own
 * `disabled` (read-only) render, where React writes `contenteditable="false"`
 * and there is no in-progress edit to protect — a click there should fall
 * through to the row toggle like any other non-interactive text.
 *
 * This selector is CLICK-only — a click target is wherever the pointer
 * lands (e.g. a title span), so an allowlist is the only way to tell "real
 * control" from "row background". KEYDOWN uses a different, stronger guard
 * below (`e.target !== e.currentTarget`): a keydown always targets the
 * focused element, so any keydown not targeting the row div itself is, by
 * construction, on a focusable descendant — this makes the keyboard half of
 * the row-swallows-a-nested-control bug class unrepresentable here
 * regardless of what this selector does or doesn't enumerate (round-2 G1/P2
 * finding: Space on the Gong cool-off checkbox collapsed the row because
 * neither the checkbox's `<span role="checkbox">` nor its `<label>` matched
 * this selector — the keydown-target guard fixes that independent of the
 * allowlist).
 */
const INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, [role="dialog"], [data-no-row-toggle], [contenteditable]:not([contenteditable="false"])';

export interface RailRowProps {
  /** `FLASH_CONTACT_ATTR` target — `QueueBody`'s landing/flash scroll effect queries rows by this (E41). Optional now — Actions rows have no contact. Omitted → no `FLASH_CONTACT_ATTR` attribute at all. */
  contactId?: string;
  /** Toggles some caller-owned boolean (e.g. `expandedItemId`). Omitted (`UnassignedRow`) → the row has no toggle semantics at all. */
  onToggle?: () => void;
  /** E28 — a busy row renders with no toggle affordance (no `role`, no `tabIndex`), same posture as `AssignedRow`'s `itemBusy` guard. */
  toggleDisabled?: boolean;
  /** Accessible name for the row when it is a button (ActionsIndex: `Open request: {who}`). */
  'aria-label'?: string;
  /** A fully-formed leading mark (e.g. a Glaze Avatar), 24×24. Wins over `icon`. */
  leading?: ReactNode;
  /** A FLOW_ICONS glyph in an unfilled 24px box, text-content-secondary. Ignored when `leading` is set. */
  icon?: ComponentProps<typeof Icon>['type'];
  /** Bold, truncated primary line — `contactDisplayIdentity(item)` (D16). */
  title: ReactNode;
  /** Secondary line(s) below the title — `rowMeta(item, playName)` plus any status/warning blocks a row wants to surface (D16). */
  meta?: ReactNode;
  /** Right-aligned slot — badge(s) + the one action button (D16–D18), or the `Assign to play…` toggle + Remove (`UnassignedRow`). */
  trailing?: ReactNode;
  /** Rendered below the header row — `LeadRow`'s `LeadExpandedBody` when `expanded`, or `UnassignedRow`'s inline `PlayPicker` when its own picker is open. */
  children?: ReactNode;
  className?: string;
  /** `card` (default) is the bordered box; `flat` is a divider-separated, full-width row whose hover is a rounded surface. */
  variant?: 'card' | 'flat';
  /** Role of the interactive element when it is clickable. `option` also emits `aria-selected`. Defaults to `button`. */
  role?: 'button' | 'option';
  /** `option` rows only: the selected surface + `aria-selected`. */
  selected?: boolean;
  /** `flat` only — rendered as a SIBLING of the interactive element (a control inside an `option` is invalid ARIA), e.g. a Stop button. */
  aside?: ReactNode;
  /** Ref to the interactive element (focus return). */
  rowRef?: Ref<HTMLDivElement>;
}

export function RailRow({
  contactId,
  onToggle,
  toggleDisabled,
  'aria-label': ariaLabel,
  leading,
  icon,
  title,
  meta,
  trailing,
  children,
  className,
  variant = 'card',
  role = 'button',
  selected,
  aside,
  rowRef,
}: RailRowProps) {
  const clickable = !!onToggle && !toggleDisabled;

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!clickable) return;
    if ((e.target as HTMLElement).closest(INTERACTIVE_SELECTOR)) return;
    onToggle?.();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!clickable) return;
    // Same guard the sibling headers use (`PlaySection.tsx`'s
    // `onHeaderKeyDown`, `account/AccountTimelineRow.tsx`'s keydown handler):
    // a keydown always targets the focused element, so a keydown whose
    // target isn't the row div itself is by construction on a focusable
    // descendant (a nested button, the Gong cool-off checkbox's span, a
    // `RichHtmlField` editor, …) and must be left alone to reach that
    // control rather than toggle the row.
    if (e.target !== e.currentTarget) return;
    // Belt-and-suspenders with the click guard above (also still needed for
    // the CLICK path, where the target is wherever the pointer landed).
    if ((e.target as HTMLElement).closest(INTERACTIVE_SELECTOR)) return;
    // Space's default (page scroll) only matters on an element that isn't
    // already a native button — this div is `role="button"`, so it must
    // prevent it itself.
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle?.();
    }
  };

  const flash = contactId !== undefined ? { [FLASH_CONTACT_ATTR]: contactId } : {};

  const header = (
    <div className={clsx('flex gap-3', variant === 'flat' ? 'min-w-0 flex-1 items-center' : 'items-start')}>
      {leading ??
        (icon && (
          <div className="flex size-6 shrink-0 items-center justify-center">
            <Icon type={icon} size="md" className="text-content-secondary" aria-hidden />
          </div>
        ))}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Strong className="truncate text-sm">{title}</Strong>
        {meta && <div className="flex flex-col gap-1 text-xs text-content-tertiary">{meta}</div>}
      </div>
      {trailing && (
        // Capped at 65% of the row: without a cap, `shrink-0` + `flex-wrap`
        // sizes this slot to its max-content width and pushes the title
        // column (the only shrinkable sibling) to 0px at the rail's 320px
        // floor. 65% leaves the title room while the badge cluster wraps.
        <div className="flex max-w-[65%] shrink-0 flex-wrap items-center justify-end gap-1.5">{trailing}</div>
      )}
    </div>
  );

  if (variant === 'flat') {
    return (
      <div {...flash} className={clsx(RAIL_ROW_DIVIDER, RAIL_ROW_INSET_Y, 'first:border-t-0', className)}>
        <div className="flex items-center gap-1.5">
          <div
            ref={rowRef}
            role={clickable ? role : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={ariaLabel}
            aria-selected={clickable && role === 'option' ? !!selected : undefined}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={clsx(
              'flex min-w-0 flex-1 items-center px-3',
              RAIL_ROW_STRIP_MIN_H,
              RAIL_ROW_SURFACE,
              clickable && 'cursor-pointer',
              selected && 'bg-bg-selected',
            )}
          >
            {header}
          </div>
          {aside}
        </div>
        {children}
      </div>
    );
  }

  return (
    <div
      {...flash}
      ref={rowRef}
      role={clickable ? role : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={ariaLabel}
      aria-selected={clickable && role === 'option' ? !!selected : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={clsx(
        'flex flex-col gap-3 rounded-lg border-[0.5px] border-border-primary bg-bg-primary p-4',
        clickable && 'cursor-pointer transition-colors hover:border-border-primary-hover',
        className,
      )}
    >
      {header}
      {children}
    </div>
  );
}
